/**
 * Lettura di ciò che finisce in un `catch`.
 *
 * In JavaScript si può lanciare qualunque cosa, quindi la variabile di un `catch` è `unknown`
 * e non un `Error`: scriverla `any` fa compilare `e.message` e `e.response.data` allo stesso
 * modo, e il secondo esplode in produzione su un errore che non è quello che si aspettava chi
 * ha scritto la riga. Questi helper leggono le proprietà che ci interessano davvero senza
 * asserire niente: se non ci sono, tornano `undefined`.
 */

/** L'oggetto errore, se è un oggetto; altrimenti un guscio vuoto (niente throw, niente cast). */
const shell = (e: unknown): Record<string, unknown> =>
  typeof e === 'object' && e !== null ? (e as Record<string, unknown>) : {}

/**
 * Il messaggio, qualunque cosa sia stata lanciata. Mai vuoto: se non c'è un `message`
 * leggibile ripiega sulla rappresentazione del valore, perché un log che dice
 * «[object Object]» è comunque più utile di uno che non dice niente.
 */
export const errMessage = (e: unknown): string => {
  // `&& e.message`: un `new Error('')` ha `message` vuoto, e senza la guardia la riga di log
  // che esiste per dire la causa non diceva niente. È anche la stessa scelta che fa già
  // `msgOf` in jobDiagnosis.ts, e ciò che l'idioma sostituito (`e?.message || e`) faceva.
  if (e instanceof Error && e.message) return e.message
  const m = shell(e).message
  if (typeof m === 'string' && m) return m
  if (typeof e === 'string' && e) return e
  return String(e)
}

/** Il `name` dell'errore (`AbortError`, `TimeoutError`, ...), se c'è. */
export const errName = (e: unknown): string | undefined => {
  const n = e instanceof Error ? e.name : shell(e).name
  return typeof n === 'string' && n ? n : undefined
}

/** Il `code` (Node, pg, SDK vari): resta stringa anche quando arriva come numero. */
export const errCode = (e: unknown): string | undefined => {
  const c = shell(e).code
  if (typeof c === 'string' && c) return c
  if (typeof c === 'number') return String(c)
  return undefined
}

/** Lo stato HTTP, dove l'SDK lo espone (`status` o `statusCode`). */
export const errStatus = (e: unknown): number | undefined => {
  const o = shell(e)
  for (const k of ['status', 'statusCode'] as const) {
    const v = o[k]
    if (typeof v === 'number') return v
    if (typeof v === 'string' && /^\d+$/.test(v)) return Number(v)
  }
  return undefined
}

/** Lo stack, per i log che lo vogliono intero. */
export const errStack = (e: unknown): string | undefined => {
  const s = e instanceof Error ? e.stack : shell(e).stack
  return typeof s === 'string' && s ? s : undefined
}
